import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faClock,
  faChevronDown,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "../styles/contact.css";
import useBusinessHours from "../utils/useBusinessHours";

const ApiURL = process.env.REACT_APP_API_URL;

const FAQS = [
  {
    q: "¿Todos los productos tienen garantía?",
    a: "Todos los productos que comercializamos poseen garantía de fabrica respaldada por el proveedor. Los plazos de cada una depende del producto.",
  },
  {
    q: "¿Tienen repuestos originales en stock?",
    a: "Contamos con un amplio stock de repuestos originales para las marcas que representamos. Si el repuesto que buscás no está disponible, lo pedimos y te avisamos cuando llega.",
  },
  {
    q: "¿Cuándo debo afilar o cambiar la cadena de mí motosierra?",
    a: "Algunos indicadores de que la cadena está desafilada son si genera aserrín y no viruta, si el corte no es preciso, o si se desvía o rebota al cortar. En estos casos es necesario afilar. Luego de varios afilados, nuestros técnicos le pueden indicar si es necesario cambiarla.",
  },
  {
    q: "¿Cómo elijo mí estufa a leña?",
    a: "Las estufas a leña tienen una capacidad calorífica según el espacio de su caja de fuegos (dónde va la leña). Lo principal es conocer la superficie a calefaccionar para saber que modelo elegir. Nuestros asesores lo guiarán en su elección.",
  },
  {
    q: "¿Los productos incluyen manual de uso?",
    a: "Sí, todos los equipos incluyen su manual de uso y recomendaciones del fabricante. Además, le brindaremos indicaciones al momento de la entrega.",
  },
];

const Contact = () => {
  const hours = useBusinessHours();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    product: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${ApiURL}/inquiries`, {
        name: formData.name,
        email: formData.email,
        message: `${formData.message}${formData.product ? `\n\nProducto de interés: ${formData.product}` : ""}\n\nTeléfono: ${formData.phone}`,
      });
      if (response.data.success) {
        navigate("/consulta-enviada", { state: { email: formData.email } });
      }
    } catch {
      toast.error("Error al enviar el mensaje. Intentá de nuevo más tarde", {
        autoClose: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="ct-page">
      <Helmet>
        <title>Contacto | Cáceres Maquinarias</title>
        <meta
          name="description"
          content="Contactate con Cáceres Maquinarias. Estamos en Blvd. Ovidio Lagos 2160, Casilda, Santa Fe. Atención de lunes a viernes y sábados por la mañana."
        />
        <meta property="og:title" content="Contacto | Cáceres Maquinarias" />
        <meta
          property="og:description"
          content="Contactate con Cáceres Maquinarias. Estamos en Blvd. Ovidio Lagos 2160, Casilda, Santa Fe."
        />
        <meta
          property="og:url"
          content="https://www.caceresmaquinarias.com.ar/contacto"
        />
        <meta
          property="og:image"
          content="https://www.caceresmaquinarias.com.ar/Logo-CM.png"
        />
      </Helmet>

      <section className="ct-hero">
        <div className="ct-hero-inner">
          <div className="ct-hero-left">
            <div className="ct-hero-badge">Contacto</div>
            <h1 className="ct-hero-title">
              Hablemos.
              <br />
              <span className="ct-hero-accent">Estamos</span> para vos.
            </h1>
            <p className="ct-hero-sub">
              Hacenos tu consulta, visitanos o escribinos por WhatsApp. Te
              asesoramos sin compromiso.
            </p>
            <div className="ct-hero-btns">
              <a
                href="https://wa.me/5493464540919?text=Hola%2C%20quiero%20hacer%20una%20consulta"
                target="_blank"
                rel="noopener noreferrer"
                className="ct-btn-wa"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
              </a>
            </div>
          </div>
          <div className="ct-hero-info">
            <div className="ct-info-card">
              <div className="ct-info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div>
                <div className="ct-info-label">Admin y ventas</div>
                <div className="ct-info-val">
                  <a
                    href="https://wa.me/5493464540919"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    3464 540919
                  </a>
                </div>
              </div>
            </div>
            <div className="ct-info-card">
              <div className="ct-info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div>
                <div className="ct-info-label">Repuestos y ventas</div>
                <div className="ct-info-val">
                  <a
                    href="https://wa.me/5493464500623"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    3464 500623
                  </a>
                </div>
              </div>
            </div>
            <div className="ct-info-card">
              <div className="ct-info-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div>
                <div className="ct-info-label">Email</div>
                <div className="ct-info-val">
                  <a href="mailto:caceresmaquinarias@gmail.com">
                    caceresmaquinarias@gmail.com
                  </a>
                </div>
              </div>
            </div>
            <div className="ct-info-card">
              <div className="ct-info-icon">
                <FontAwesomeIcon icon={faLocationDot} />
              </div>
              <div>
                <div className="ct-info-label">Dirección</div>
                <div className="ct-info-val">
                  Blvd. Ovidio Lagos 2160
                  <br />
                  Casilda, Santa Fe
                </div>
              </div>
            </div>
            <div className="ct-info-card">
              <div className="ct-info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div>
                <div className="ct-info-label">Horarios</div>
                <div className="ct-info-val">
                  {hours ? (
                    <>
                      Lun–Vie: {hours.weekdays}
                      <br />
                      Sáb: {hours.saturday}
                    </>
                  ) : (
                    "Cargando..."
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="ct-main">
        <div className="ct-map-side">
          <div className="ct-side-label">Dónde encontrarnos</div>
          <h2 className="ct-side-title">Visitanos en Casilda</h2>
          <div className="ct-map-embed">
            <iframe
              title="Ubicación Cáceres Maquinarias"
              src="https://maps.google.com/maps?q=Blvd.+Ovidio+Lagos+2160,+Casilda,+Santa+Fe&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <a
            href="https://maps.google.com/?q=Blvd.+Ovidio+Lagos+2160,+Casilda,+Santa+Fe"
            target="_blank"
            rel="noopener noreferrer"
            className="ct-maps-btn"
          >
            <FontAwesomeIcon icon={faLocationDot} /> Ver en Google Maps
          </a>
        </div>

        <div className="ct-form-side">
          <div className="ct-side-label">Escribinos</div>
          <h2 className="ct-side-title">Envianos tu consulta</h2>
          <p className="ct-form-sub">
            Completá el formulario y te respondemos en menos de 24 horas.
          </p>

          <form className="ct-form" onSubmit={handleSubmit}>
            <div className="ct-form-grid">
              <div className="ct-form-group">
                <label>Nombre completo</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
              <div className="ct-form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
              <div className="ct-form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
              <div className="ct-form-group">
                <label>Producto de interés</label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                >
                  <option value="">— Seleccionar —</option>
                  <option>Motosierras</option>
                  <option>Cortadoras de césped</option>
                  <option>Bordeadoras / Motoguadañas</option>
                  <option>Tractores cortacésped</option>
                  <option>Hidrolavadoras</option>
                  <option>Repuestos</option>
                  <option>Garantía</option>
                  <option>Otro</option>
                </select>
              </div>
              <div className="ct-form-group ct-form-full">
                <label>Mensaje</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Contanos en qué podemos ayudarte..."
                  required
                />
              </div>
            </div>
            <button type="submit" className="ct-submit" disabled={isSubmitting}>
              <FontAwesomeIcon icon={faPaperPlane} />
              {isSubmitting ? "Enviando..." : "Enviar mensaje"}
            </button>
          </form>

          <div className="ct-or">o contactanos directamente por</div>
          <div className="ct-socials">
            <a
              href="https://wa.me/5493464540919"
              target="_blank"
              rel="noopener noreferrer"
              className="ct-soc-btn"
            >
              <FontAwesomeIcon icon={faWhatsapp} style={{ color: "#25D366" }} />{" "}
              WhatsApp
            </a>
            <a
              href="https://www.instagram.com/caceresmaquinarias/"
              target="_blank"
              rel="noopener noreferrer"
              className="ct-soc-btn"
            >
              <FontAwesomeIcon
                icon={faInstagram}
                style={{ color: "#E1306C" }}
              />{" "}
              Instagram
            </a>
            <a
              href="https://www.facebook.com/caceresmaq"
              target="_blank"
              rel="noopener noreferrer"
              className="ct-soc-btn"
            >
              <FontAwesomeIcon icon={faFacebook} style={{ color: "#1877F2" }} />{" "}
              Facebook
            </a>
          </div>
        </div>
      </div>

      <div className="ct-wa-band">
        <div>
          <h2 className="ct-wa-title">¿Preferís escribirnos directo?</h2>
          <p className="ct-wa-sub">
            Respondemos por WhatsApp de lunes a sábado en horario comercial. Sin
            esperas, sin formularios.
          </p>
        </div>
        <a
          href="https://wa.me/5493464540919?text=Hola%2C%20quiero%20hacer%20una%20consulta"
          target="_blank"
          rel="noopener noreferrer"
          className="ct-wa-big-btn"
        >
          <FontAwesomeIcon icon={faWhatsapp} /> Escribirnos por WhatsApp
        </a>
      </div>

      <section className="ct-faq">
        <div className="ct-side-label">Preguntas frecuentes</div>
        <h2 className="ct-faq-title">Lo que nos suelen preguntar</h2>
        <div className="ct-faq-list">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`ct-faq-item${openFaq === i ? " open" : ""}`}
            >
              <button className="ct-faq-q" onClick={() => toggleFaq(i)}>
                {faq.q}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="ct-faq-chevron"
                />
              </button>
              <div className="ct-faq-a">{faq.a}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
